import {isUndefined} from 'lodash';

import {AnalyticsMultiGraphMetricsEnum} from '../../../../../models/api/analyticsMetricsModel';
import {SeparatorsEnum} from '../../../../helpers/analyticsExportHelpers';
import {getMetricLabels} from '../../../../helpers/metricLabels';
import {DataAccessor} from '../../../charts/commons/accessors';
import {ChartSerie} from '../../../charts/commons/types';
import {AnalyticsDataPointValueType, computeCsvQuote} from '../../../exports/helpers/metricExports';

/**
 * Helper function that returns a closure that when called with a separator will generate a
 * CSV formatted string of the chart series data (used in the line chart and bar chart).
 * Used for exporting the metric level data from an individual chart
 */
export function generateCsvFormattedData<T>(
  xAccessor: DataAccessor<T>,
  chartSeries: ReadonlyArray<ChartSerie<T>>,
  data: ReadonlyArray<T>,
  selectedMetric?: AnalyticsMultiGraphMetricsEnum,
) {
  return (separator: SeparatorsEnum) => {
    const computeQuoteValue = (value: AnalyticsDataPointValueType) => computeCsvQuote(value, separator);
    const result = [];

    const headerRow = generateCsvHeaderRow(computeQuoteValue, selectedMetric);
    result.push(headerRow.join(separator));

    const rowDates = data.map((d) => xAccessor(d));
    const yAccessors = chartSeries.map((serie) => (d: T) => serie.accessor(d));
    const rows = data.map((d) => yAccessors.map((yAccessor) => computeQuoteValue(yAccessor(d))));

    rows.forEach((row, index) =>
      result.push([computeQuoteValue(rowDates[index])].concat(row).join(separator)),
    );
    return result.join('\n');
  };
}

function generateCsvHeaderRow(
  computeQuoteValue: (value: AnalyticsDataPointValueType) => AnalyticsDataPointValueType,
  selectedMetric?: AnalyticsMultiGraphMetricsEnum,
): AnalyticsDataPointValueType[] {
  const labels = getMetricLabels(selectedMetric);
  const {series} = labels;

  // First column is always the date
  const labelsBase: AnalyticsDataPointValueType[] = ['Date'];

  if (isUndefined(series)) {
    return labelsBase.concat([computeQuoteValue(labels.label) || '']);
  }

  return labelsBase.concat(series.map((label) => computeQuoteValue(label.label)));
}
